import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2,
  ArrowLeft,
  Edit,
  Trash2,
  Link2,
  User,
  ExternalLink,
  Calendar,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useApplicationDetail } from '../services/queries';
import {
  useDeleteApplication,
  useUpdateApplication,
  useSetStatusApplication,
} from '../services/mutations';
import { toast } from 'react-hot-toast';
import ConfirmationModal from '../components/ConfirmationModal';
import FormDrawer from '../components/FormDrawer';
import useAuth from '../hooks/useAuth';

const ApplicationDetail = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { checkPermissionElement } = useAuth();
  const {
    data: appDetail,
    isLoading,
    error,
    refetch,
  } = useApplicationDetail(code);
  const deleteApplication = useDeleteApplication();
  const updateApplication = useUpdateApplication();
  const setStatusApplication = useSetStatusApplication();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [form, setForm] = useState({
    code: '',
    name: '',
    host: '',
    auth_uri: '/',
    health_uri: '/health',
    description: '',
    is_active: true,
    has_credentials: false,
    credentials: {
      username: '',
      password: '',
    },
  });
  const [formError, setFormError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (code) {
      refetch();
    }
  }, [code, refetch]);

  // Initialize form when app data is loaded
  useEffect(() => {
    if (appDetail?.data) {
      const app = appDetail.data;
      setForm({
        code: app.code || '',
        name: app.name || '',
        host: app.host || '',
        auth_uri: app.auth_uri || '/',
        health_uri: app.health_uri || '/health',
        description: app.description || '',
        is_active: app.is_active ?? true,
        has_credentials: app.has_credentials ?? false,
        credentials: app.credentials || {
          username: '',
          password: '',
        },
      });
    }
  }, [appDetail]);

  const handleBack = () => {
    navigate('/applications');
  };

  const handleEdit = () => {
    setIsEditOpen(true);
  };

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleStatusChange = (value) => {
    setForm((prevForm) => ({ ...prevForm, is_active: value === 'true' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSubmitLoading(true);
    const newForm = { ...form };
    if (!form.credentials.username || !form.credentials.password)
      delete newForm['credentials'];
    try {
      // Separate status update from other fields
      const { is_active, ...updateBody } = newForm;
      
      // Update other fields
      await updateApplication.mutateAsync({
        code: code,
        body: updateBody,
      });
      
      // Update status separately using the status endpoint
      await setStatusApplication.mutateAsync({
        code: code,
        is_active: Boolean(is_active),
      });
      
      setIsEditOpen(false);
      refetch();
      toast.success('Application updated successfully');
    } catch (error) {
      setFormError(error.message);
      toast.error('Failed to update application');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = () => {
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await deleteApplication.mutateAsync(code);
      toast.success('Application deleted successfully');
      setIsModalOpen(false);
      navigate('/applications');
    } catch (error) {
      console.error('Error during application delete:', error);
      toast.error('Failed to delete application');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center mt-10 page-shell">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-shell">
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>
            {error.message || 'Failed to load application details'}
          </AlertDescription>
        </Alert>
        <Button
          onClick={handleBack}
          className="mt-2 flex items-center gap-2"
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
      </div>
    );
  }

  const app = appDetail?.data;

  if (!app) {
    return (
      <div className="page-shell">
        <Alert variant="destructive" className="mt-2">
          <AlertDescription>Application not found</AlertDescription>
        </Alert>
        <Button
          onClick={handleBack}
          className="mt-2 flex items-center gap-2"
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Applications
        </Button>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Application Detail</h1>
            <p className="text-muted-foreground text-sm">
              Manage application credentials and status
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {checkPermissionElement(
            'superapp:application-management:button:edit',
          ) && (
            <Button
              variant="outline"
              onClick={handleEdit}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
          {checkPermissionElement(
            'superapp:application-management:button:delete',
          ) && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ExternalLink className="h-5 w-5" /> Application Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Name
                    </label>
                    <p className="mt-1 font-medium text-lg">
                      {app.name || '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Code
                    </label>
                    <p className="mt-1 font-mono bg-muted px-2 py-1 rounded w-fit">
                      {app.code || '-'}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Description
                  </label>
                  <p className="mt-1">{app.description || '-'}</p>
                </div>

                <div className="grid grid-cols-1 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase">
                        Host
                      </label>
                      <div className="flex items-center gap-1 mt-1">
                        <Link2 className="h-3 w-3 text-muted-foreground" />
                        <span className="font-medium text-sm break-all">
                          {app.host || '-'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase">
                        Auth URI
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {app.auth_uri || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase">
                        Health URI
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {app.health_uri || '-'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      Status
                    </label>
                    <Badge
                      variant={app.is_active ? 'default' : 'secondary'}
                      className={
                        app.is_active
                          ? 'bg-green-600 hover:bg-green-700 w-fit'
                          : 'w-fit'
                      }
                    >
                      {app.is_active ? 'Aktif' : 'Nonaktif'}
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      Type
                    </label>
                    <Badge variant="outline" className="w-fit">
                      {app.is_external ? 'External' : 'Internal'}
                    </Badge>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-medium text-muted-foreground">
                      Credentials
                    </label>
                    <Badge
                      variant={app.has_credentials ? 'default' : 'outline'}
                      className="w-fit"
                    >
                      {app.has_credentials
                        ? 'Has Credentials'
                        : 'No Credentials'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profiles Section */}
            {app.profiles && app.profiles.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5" /> Associated Profiles
                  </CardTitle>
                  <CardDescription>
                    Users associated with this application
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Username</TableHead>
                          <TableHead>Full Name</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {app.profiles.map((profile, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">
                              {profile.username}
                            </TableCell>
                            <TableCell>{profile.full_name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar Metadata */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" /> Metadata
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Created At
                  </label>
                  <p className="text-sm font-medium">
                    {formatDate(app.created_at)}
                  </p>
                </div>
                <Separator />
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Updated At
                  </label>
                  <p className="text-sm font-medium">
                    {formatDate(app.updated_at)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        description={`Are you sure you want to delete application "${app.name}"?`}
        loading={loading}
      />

      <FormDrawer isOpen={isEditOpen} setIsOpen={setIsEditOpen}>
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[500px] flex flex-col h-full"
        >
          <div>
            <h3 className="text-xl font-semibold">Edit Application</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Update application details and settings.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {formError && (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Application Name</Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChangeInput}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                name="code"
                value={form.code}
                onChange={handleChangeInput}
                required
                disabled
                className="bg-muted text-muted-foreground"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="host">Application URL</Label>
              <Input
                id="host"
                name="host"
                value={form.host}
                onChange={handleChangeInput}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="auth_uri">Login Path</Label>
              <Input
                id="auth_uri"
                name="auth_uri"
                value={form.auth_uri}
                onChange={handleChangeInput}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleChangeInput}
                rows={4}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={form.is_active ? 'true' : 'false'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[1400]">
                  <SelectItem value="true">Aktif</SelectItem>
                  <SelectItem value="false">Nonaktif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="has_credentials"
                  checked={form.has_credentials}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, has_credentials: Boolean(checked) })
                  }
                />
                <Label
                  htmlFor="has_credentials"
                  className="text-sm font-medium cursor-pointer"
                >
                  Has Credentials?
                </Label>
              </div>

              {form?.has_credentials && (
                <div className="space-y-3 pt-2 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="username">Username/Email/NIK</Label>
                    <Input
                      id="username"
                      value={form?.credentials?.username || ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          credentials: {
                            ...form.credentials,
                            username: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={form?.credentials?.password || ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          credentials: {
                            ...form.credentials,
                            password: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pt-3 border-t bg-muted/10 flex justify-end gap-3">
            <Button
              variant="ghost"
              type="button"
              onClick={() => setIsEditOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitLoading}>
              {submitLoading && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Update Application
            </Button>
          </div>
        </form>
      </FormDrawer>
    </div>
  );
};

export default ApplicationDetail;
